import type { ReactNode } from 'react'

/* ------------------------------------------------------------------ *
 * Formatting
 *
 * Money is integer cents everywhere on the wire; these are the only places it
 * becomes a string, so a rounding choice is made once.
 * ------------------------------------------------------------------ */

export function cents(value: number, opts: { compact?: boolean; sign?: boolean } = {}): string {
  const n = value / 100
  const sign = opts.sign && value > 0 ? '+' : ''
  if (opts.compact && Math.abs(n) >= 10_000) {
    return `${sign}$${(n / 1000).toFixed(1)}k`
  }
  return `${sign}${n < 0 ? '−' : ''}$${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

export function percent(value: number | null, dp = 1): string {
  return value === null ? '—' : `${(value * 100).toFixed(dp)}%`
}

export function multiple(value: number): string {
  return `${value.toFixed(2)}×`
}

export function shortAddress(a: string): string {
  return `${a.slice(0, 6)}…${a.slice(-4)}`
}

export function shortHash(h: string, n = 8): string {
  return `${h.slice(0, n)}…${h.slice(-4)}`
}

export function timeAgo(iso: string): string {
  const seconds = (Date.now() - new Date(iso).getTime()) / 1000
  if (seconds < 60) return `${Math.floor(seconds)}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h`
  return `${Math.floor(seconds / 86_400)}d`
}

export function dateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/* ------------------------------------------------------------------ *
 * Furniture
 * ------------------------------------------------------------------ */

export function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string
  value: ReactNode
  sub?: ReactNode
  tone?: 'up' | 'down' | 'warn'
}) {
  const color =
    tone === 'up'
      ? 'var(--color-up)'
      : tone === 'down'
        ? 'var(--color-down)'
        : tone === 'warn'
          ? 'var(--color-warn)'
          : undefined
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className="tnum mt-1 text-2xl font-bold" style={{ color }}>
        {value}
      </div>
      {sub !== undefined && (
        <div className="mt-1 text-xs" style={{ color: 'var(--color-faint)' }}>
          {sub}
        </div>
      )}
    </div>
  )
}

export function Panel({
  title,
  action,
  children,
  className = '',
}: {
  title?: string
  action?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={`panel ${className}`}>
      {(title || action) && (
        <header className="flex items-center justify-between gap-4 border-b px-4 py-3">
          {title && <h2 className="text-sm font-semibold">{title}</h2>}
          {action}
        </header>
      )}
      {children}
    </section>
  )
}

const STATUS_TONE: Record<string, string> = {
  credited: 'var(--color-up)',
  confirmed: 'var(--color-up)',
  approved: 'var(--color-info)',
  broadcast: 'var(--color-info)',
  signed: 'var(--color-info)',
  seen: 'var(--color-muted)',
  requested: 'var(--color-warn)',
  unattributed: 'var(--color-warn)',
  failed: 'var(--color-down)',
  rejected: 'var(--color-faint)',
  ignored: 'var(--color-faint)',
}

export function StatusChip({ status }: { status: string }) {
  const tone = STATUS_TONE[status] ?? 'var(--color-muted)'
  return (
    <span
      className="chip"
      style={{ color: tone, background: `color-mix(in srgb, ${tone} 14%, transparent)` }}
    >
      {status}
    </span>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="px-4 py-10 text-center text-sm" style={{ color: 'var(--color-faint)' }}>
      {children}
    </div>
  )
}

export function Loading() {
  return (
    <div className="px-4 py-10 text-center text-sm" style={{ color: 'var(--color-faint)' }}>
      Loading…
    </div>
  )
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      className="panel px-4 py-3 text-sm"
      style={{ borderColor: 'color-mix(in srgb, var(--color-down) 45%, transparent)', color: 'var(--color-down)' }}
    >
      {children}
    </div>
  )
}

/** A number that should be read against a threshold, coloured when it is over. */
export function Watch({
  value,
  warn,
  children,
}: {
  value: number
  warn: number
  children: ReactNode
}) {
  return (
    <span style={{ color: value >= warn ? 'var(--color-warn)' : undefined }}>{children}</span>
  )
}
